import { Pressable, Text, View } from "react-native";
import { ChevronRight, MapPin, Plus } from "lucide-react-native";
import { CardSubtitleText, CardTitleText, StatusBadge } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import { useTranslations, useLocale } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n/translations";
import {
  getRemainingOocyteCohortCount,
  useFrozenSpecimens,
  type OocyteCohort,
  type Specimen,
} from "./useSpecimens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const TEXT = EMBER.colors.text;
const TEXT_MID = EMBER.colors.textMid;
const TEXT_LIGHT = EMBER.colors.textLight;

export type CryoSpecimenSnapshot = {
  id: string;
  label: string;
  count: string;
  facility: string;
  renewal: string;
  detail: string;
  status: string;
  organicIcon: OrganicMiniIconKind;
  specimenIds: string[];
};

export type CryoVaultCarouselItem = {
  kind: "specimen";
  specimen: CryoSpecimenSnapshot;
};

function formatShortDate(date: string | null | undefined, locale: string) {
  if (!date) return "";
  const parsed = new Date(`${date.split("T")[0]}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
}

export function buildCryoSpecimenSnapshotsFromStorageGroups(
  t: TranslationFunction,
  locale: string,
  storageGroups: ReturnType<typeof useFrozenSpecimens>["storageGroups"],
  cohorts: readonly OocyteCohort[] = [],
): CryoSpecimenSnapshot[] {
  const cohortByMemberId = new Map<string, OocyteCohort>();
  for (const cohort of cohorts) {
    for (const member of cohort.members) {
      cohortByMemberId.set(member.id, cohort);
    }
  }

  const snapshots: CryoSpecimenSnapshot[] = [];
  const cohortSnapshots = new Map<string, CryoSpecimenSnapshot>();

  for (const [index, group] of storageGroups.entries()) {
    const cohort =
      group.type === "oocyte"
        ? group.specimens
            .map((specimen) => cohortByMemberId.get(specimen.id))
            .find((candidate): candidate is OocyteCohort => candidate != null)
        : undefined;

    if (cohort) {
      const count = getRemainingOocyteCohortCount(
        cohort.numberFrozen,
        cohort.numberThawed,
        cohort.members.length,
      );
      const existing = cohortSnapshots.get(cohort.id);
      if (existing) {
        existing.specimenIds = [
          ...new Set([...existing.specimenIds, ...group.specimenIds]),
        ];
        continue;
      }

      const typeLabel =
        count === 1
          ? t("care.cryo_vault.overview.type_egg_singular")
          : t("care.cryo_vault.overview.type_egg_plural");
      const storedDate = formatShortDate(group.storedDate, locale);
      const collectedDate = formatShortDate(group.collectedAt, locale);
      const snapshot: CryoSpecimenSnapshot = {
        id: `oocyte-cohort-${cohort.id}`,
        label: typeLabel,
        count: String(count),
        facility: group.facility,
        renewal: t("care.cryo_vault.overview.renewal_not_set"),
        detail: storedDate
          ? t("care.cryo_vault.overview.detail_stored_since", {
              params: { date: storedDate },
            })
          : collectedDate
            ? t("care.cryo_vault.overview.detail_collected", {
                params: { date: collectedDate },
              })
            : t("care.cryo_vault.overview.detail_default"),
        status: t("care.cryo_vault.overview.status_stored"),
        organicIcon: "eggs",
        specimenIds: [...group.specimenIds],
      };
      cohortSnapshots.set(cohort.id, snapshot);
      continue;
    }

    const count = group.specimens.length;
    const typeLabel =
      group.type === "oocyte"
        ? count === 1
          ? t("care.cryo_vault.overview.type_egg_singular")
          : t("care.cryo_vault.overview.type_egg_plural")
        : group.type === "embryo"
          ? count === 1
            ? t("care.cryo_vault.overview.type_embryo_singular")
            : t("care.cryo_vault.overview.type_embryo_plural")
          : count === 1
            ? t("care.cryo_vault.overview.type_sperm_singular")
            : t("care.cryo_vault.overview.type_sperm_plural");
    const storedDate = formatShortDate(group.storedDate, locale);
    const collectedDate = formatShortDate(group.collectedAt, locale);

    snapshots.push({
      id: `${group.type}-${group.facility}-${index}`,
      label: typeLabel,
      count: String(count),
      facility: group.facility,
      renewal: t("care.cryo_vault.overview.renewal_not_set"),
      detail: storedDate
        ? t("care.cryo_vault.overview.detail_stored_since", {
            params: { date: storedDate },
          })
        : collectedDate
          ? t("care.cryo_vault.overview.detail_collected", {
              params: { date: collectedDate },
            })
          : t("care.cryo_vault.overview.detail_default"),
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon:
        group.type === "oocyte"
          ? "eggs"
          : group.type === "embryo"
            ? "genetics"
            : "sperm",
      specimenIds: group.specimenIds,
    });
  }

  return [...snapshots, ...cohortSnapshots.values()];
}

/**
 * Snapshot builder that mirrors the CryoVault screen's own selection: every
 * specimen the API returns, minus those already represented by an oocyte
 * cohort, plus one snapshot per cohort. Deliberately does not apply the
 * "verified frozen storage" filter used by useFrozenSpecimens — the Care
 * overview must not silently disagree with the vault the patient just opened.
 */
export function buildCryoSpecimenSnapshotsFromApiSpecimens(
  t: TranslationFunction,
  locale: string,
  apiSpecimens: readonly Specimen[],
  cohorts: readonly OocyteCohort[] = [],
): CryoSpecimenSnapshot[] {
  const cohortMemberIds = new Set(
    cohorts.flatMap((cohort) => cohort.members.map((member) => member.id)),
  );
  const individualSpecimens = apiSpecimens.filter(
    (specimen) => !cohortMemberIds.has(specimen.id),
  );

  const individualSnapshots: CryoSpecimenSnapshot[] = individualSpecimens.map(
    (specimen) => {
      const activeLocation =
        specimen.storageLocations.find((loc) => loc.isActive) ??
        specimen.storageLocations[0];
      const facility = activeLocation?.facility ?? "";
      const storedFrom = activeLocation?.storedFrom ?? null;
      const storedDate = formatShortDate(storedFrom, locale);
      const collectedDate = formatShortDate(specimen.collectedAt, locale);
      const label =
        specimen.type === "oocyte"
          ? t("care.cryo_vault.overview.type_egg_singular")
          : specimen.type === "embryo"
            ? t("care.cryo_vault.overview.type_embryo_singular")
            : t("care.cryo_vault.overview.type_sperm_singular");
      const organicIcon: OrganicMiniIconKind =
        specimen.type === "oocyte"
          ? "eggs"
          : specimen.type === "embryo"
            ? "genetics"
            : "sperm";
      return {
        id: `specimen-${specimen.id}`,
        label,
        count: "1",
        facility,
        renewal: t("care.cryo_vault.overview.renewal_not_set"),
        detail: storedDate
          ? t("care.cryo_vault.overview.detail_stored_since", {
              params: { date: storedDate },
            })
          : collectedDate
            ? t("care.cryo_vault.overview.detail_collected", {
                params: { date: collectedDate },
              })
            : t("care.cryo_vault.overview.detail_default"),
        status: t("care.cryo_vault.overview.status_stored"),
        organicIcon,
        specimenIds: [specimen.id],
      };
    },
  );

  const cohortSnapshots: CryoSpecimenSnapshot[] = cohorts.map((cohort) => {
    const count = getRemainingOocyteCohortCount(
      cohort.numberFrozen,
      cohort.numberThawed,
      cohort.members.length,
    );
    const typeLabel =
      count === 1
        ? t("care.cryo_vault.overview.type_egg_singular")
        : t("care.cryo_vault.overview.type_egg_plural");
    const activeMember = cohort.members.find(
      (member) => member.storageFacility || member.frozenDate,
    );
    const facility = activeMember?.storageFacility ?? "";
    const frozenDate = activeMember?.frozenDate ?? null;
    const storedDate = formatShortDate(frozenDate, locale);
    return {
      id: `oocyte-cohort-${cohort.id}`,
      label: typeLabel,
      count: String(count),
      facility,
      renewal: t("care.cryo_vault.overview.renewal_not_set"),
      detail: storedDate
        ? t("care.cryo_vault.overview.detail_stored_since", {
            params: { date: storedDate },
          })
        : t("care.cryo_vault.overview.detail_default"),
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "eggs",
      specimenIds: cohort.members.map((member) => member.id),
    };
  });

  return [...individualSnapshots, ...cohortSnapshots];
}

export function CryoSpecimenCarouselCard({
  specimen,
  width,
  equalizedHeight,
  onPress,
}: {
  specimen: CryoSpecimenSnapshot;
  width: number;
  equalizedHeight?: number;
  onPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoSpecimenCarouselCard");
  const storedStatus = t("care.cryo_vault.overview.status_stored");
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(`${specimen.count} ${specimen.label}`),
          "specimen_card",
        );
        onPress?.();
      }}
      disabled={!onPress}
      accessibilityRole={onPress ? "button" : undefined}
      accessibilityLabel={`${specimen.count} ${specimen.label} — ${specimen.facility}`}
      style={({ pressed }) => ({
        width,
        minHeight: equalizedHeight,
        borderRadius: 14,
        backgroundColor: "rgba(255,255,255,0.38)",
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        padding: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: pressed && onPress ? 0.74 : 1,
      })}
    >
      <OrganicMiniIcon kind={specimen.organicIcon} size={32} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {specimen.count} {specimen.label}
        </Text>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginTop: 2,
          }}
        >
          <MapPin size={11} color={TEXT_LIGHT} strokeWidth={1.8} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              fontSize: 10.5,
              lineHeight: 13,
              color: TEXT_LIGHT,
              fontWeight: "700",
              flex: 1,
            }}
          >
            {specimen.facility}
          </Text>
        </View>
      </View>
      {specimen.status !== storedStatus ? (
        <StatusBadge label={specimen.status} />
      ) : null}
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.7} />
    </Pressable>
  );
}

export function CryoVaultEmptyState({
  onAddSpecimen,
  onNoSpecimens,
}: {
  onAddSpecimen: () => void;
  onNoSpecimens: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoVaultEmptyState");
  const specimenTypes = [
    t("care.cryo_vault.overview.empty_chip_eggs"),
    t("care.cryo_vault.overview.empty_chip_embryos"),
    t("care.cryo_vault.overview.empty_chip_sperm"),
    t("care.cryo_vault.overview.empty_chip_facility"),
    t("care.cryo_vault.overview.empty_chip_reports"),
  ];

  return (
    <View
      style={{
        borderRadius: 18,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: "rgba(92,52,32,0.16)",
        backgroundColor: "rgba(255,255,255,0.32)",
        padding: 14,
        gap: 12,
      }}
    >
      <View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 19,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {t("care.cryo_vault.overview.empty_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12.5,
            lineHeight: 17,
            color: TEXT_MID,
            marginTop: 4,
          }}
        >
          {t("care.cryo_vault.overview.empty_subtitle")}
        </Text>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
        {specimenTypes.map((type) => (
          <View
            key={type}
            style={{
              borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.42)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.08)",
              paddingHorizontal: 9,
              paddingVertical: 6,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10.5,
                lineHeight: 13,
                color: TEXT_LIGHT,
                fontWeight: "800",
              }}
            >
              {type}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <Pressable
          onPress={() => {
            capturePress(
              t("care.cryo_vault.overview.empty_none_button"),
              "care.cryo_vault.overview.empty_none_button",
            );
            onNoSpecimens();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("care.cryo_vault.overview.empty_none_a11y")}
          style={({ pressed }) => ({
            flex: 1,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.38)",
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.08)",
            paddingVertical: 10,
            paddingHorizontal: 12,
            justifyContent: "center",
            opacity: pressed ? 0.74 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11.5,
              lineHeight: 15,
              color: TEXT_LIGHT,
              fontWeight: "800",
              textAlign: "center",
            }}
          >
            {t("care.cryo_vault.overview.empty_none_button")}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => {
            capturePress(
              t("care.cryo_vault.overview.empty_add_button"),
              "care.cryo_vault.overview.empty_add_button",
            );
            onAddSpecimen();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("care.cryo_vault.overview.empty_add_a11y")}
          style={({ pressed }) => ({
            flex: 1,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.72)",
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.1)",
            paddingVertical: 10,
            paddingHorizontal: 12,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            opacity: pressed ? 0.74 : 1,
          })}
        >
          <Plus size={13} color={TEXT_MID} strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11.5,
              lineHeight: 15,
              color: TEXT_MID,
              fontWeight: "900",
              textAlign: "center",
            }}
          >
            {t("care.cryo_vault.overview.empty_add_button")}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function buildFixtureCryoSpecimenSnapshots(
  t: TranslationFunction,
): CryoSpecimenSnapshot[] {
  return [
    {
      id: "stored-eggs",
      label: t("care.cryo_vault.overview.type_egg_plural"),
      count: "11",
      facility: "NY Cryo",
      renewal: "May 2027",
      detail: "Egg freezing round, Jan 2025",
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "eggs",
      specimenIds: [],
    },
    {
      id: "stored-embryos",
      label: t("care.cryo_vault.overview.type_embryo_plural"),
      count: "4",
      facility: "Future Fertility Lab",
      renewal: "Aug 2026",
      detail: "PGT-A report expected after biopsy results",
      status: t("care.cryo_vault.overview.status_report_pending"),
      organicIcon: "genetics",
      specimenIds: [],
    },
    {
      id: "sperm-backup",
      label: t("care.cryo_vault.overview.type_sperm_plural"),
      count: "3",
      facility: "CryoBank West",
      renewal: "Mar 2027",
      detail: "Partner specimen linked to this plan",
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "sperm",
      specimenIds: [],
    },
  ];
}

export function CryoVaultOverviewCard({
  onPress,
  onAddSpecimen,
  onNoSpecimens,
  onPressSpecimen,
  specimens,
}: {
  onPress: () => void;
  onAddSpecimen: () => void;
  onNoSpecimens: () => void;
  onPressSpecimen?: (specimen: CryoSpecimenSnapshot) => void;
  specimens?: CryoSpecimenSnapshot[];
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoVaultOverviewCard");
  const effectiveSpecimens = specimens ?? buildFixtureCryoSpecimenSnapshots(t);
  const hasSpecimens = effectiveSpecimens.length > 0;
  const facilityCount = new Set(
    effectiveSpecimens.map((specimen) => specimen.facility),
  ).size;
  const totalSpecimens = effectiveSpecimens.reduce(
    (sum, specimen) => sum + (parseInt(specimen.count, 10) || 0),
    0,
  );
  const carouselItems: CryoVaultCarouselItem[] = effectiveSpecimens.map(
    (specimen) => ({
      kind: "specimen" as const,
      specimen,
    }),
  );

  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        borderRadius: 18,
        padding: 15,
        gap: 13,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("care.cryo_vault.overview.open_a11y")),
              "open_cryo_vault",
            );
            onPress();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("care.cryo_vault.overview.open_a11y")}
          style={({ pressed }) => ({
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            opacity: pressed ? 0.74 : 1,
          })}
        >
          <OrganicMiniIcon kind="cryo" size={42} />
          <View style={{ flex: 1 }}>
            <CardTitleText>
              {hasSpecimens
                ? t(
                    totalSpecimens === 1
                      ? "care.cryo_vault.overview.title_count_one"
                      : "care.cryo_vault.overview.title_count_other",
                    { params: { count: String(totalSpecimens) } },
                  )
                : t("care.cryo_vault.overview.title_empty")}
            </CardTitleText>
            <CardSubtitleText>
              {hasSpecimens
                ? t(
                    facilityCount === 1
                      ? "care.cryo_vault.overview.subtitle_one_facility"
                      : "care.cryo_vault.overview.subtitle_other_facility",
                    { params: { count: String(facilityCount) } },
                  )
                : t("care.cryo_vault.overview.subtitle_empty")}
            </CardSubtitleText>
          </View>
          <ChevronRight size={15} color={TEXT_LIGHT} strokeWidth={1.8} />
        </Pressable>
        <Pressable
          onPress={() => {
            capturePress("add_specimen");
            onAddSpecimen();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("care.cryo_vault.overview.empty_add_a11y")}
          hitSlop={6}
          style={({ pressed }) => ({
            width: 30,
            height: 30,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.12)",
            backgroundColor: "rgba(255,255,255,0.5)",
            alignItems: "center",
            justifyContent: "center",
            opacity: pressed ? 0.66 : 1,
          })}
        >
          <Plus size={15} color={TEXT_MID} strokeWidth={2} />
        </Pressable>
      </View>

      {carouselItems.length > 0 ? (
        <HorizontalSnapCarousel
          items={carouselItems}
          gap={9}
          fallbackCardWidth={198}
          getCardWidth={(containerWidth) =>
            Math.min(218, Math.max(184, containerWidth * 0.48))
          }
          keyExtractor={(item) => item.specimen.id}
          measureKey={carouselItems.map((item) => item.specimen.id).join("|")}
          renderItem={(item, width, _index, equalizedHeight) => (
            <CryoSpecimenCarouselCard
              specimen={item.specimen}
              width={width}
              equalizedHeight={equalizedHeight}
              onPress={
                onPressSpecimen ? () => onPressSpecimen(item.specimen) : onPress
              }
            />
          )}
        />
      ) : (
        <CryoVaultEmptyState
          onAddSpecimen={onAddSpecimen}
          onNoSpecimens={onNoSpecimens}
        />
      )}
    </View>
  );
}

// Hook for callers that need to build snapshots from storage groups
// while staying inside the React render flow (uses translations + locale).
export function useCryoSpecimenSnapshotsBuilder() {
  const t = useTranslations();
  const { locale } = useLocale();
  const bcp47 = locale === "fr" ? "fr-FR" : "en-US";
  return (
    storageGroups: ReturnType<typeof useFrozenSpecimens>["storageGroups"],
    cohorts: readonly OocyteCohort[] = [],
  ) =>
    buildCryoSpecimenSnapshotsFromStorageGroups(
      t,
      bcp47,
      storageGroups,
      cohorts,
    );
}
